import './Header.css';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import useScroll from 'Hooks/useScroll';
import { Logo } from 'Components/Logo';
import { SmartLink } from 'Components/SmartLink';
import UserMenu from 'Features/UserMenu';
import { RedfastZoneByPathname } from 'Components/RedfastZone';
import { HTMLParser } from 'Utils/Parser';
import { MobileWebConfirmationHeader } from 'Components/MobileWebConfirmationHeader/MobileWebConfirmationHeader';
import { device as isMobile } from 'device.js';
import { isDefaultPlanFlow } from 'Utils/Analytics/Amplitude';

const getHeaderRouteStateByPathname = (pathname = '') => ({
  isPurchaseFunnelRoute: /signup|signin|shipping|payment/g.test(pathname) && !/signup-confirmation/g.test(pathname),
  isGiftingRoute: /review-and-redeem|redeem-confirmation/g.test(pathname),
  isPromoBannerRoute: /signup-plans|shipping|payment/g.test(pathname),
  isNoSignInButtonRoute: /reset-by-email|mvpd|bango-final/g.test(pathname),
  isNoSignUpButtonRoute: /signup-plans/g.test(pathname),
});

//Per sign up flow redesign experiment. If becomes permanent, add to isNoSignUpButtonRoute above
const noSignUpButtonPerRedesign = (pathname = '') => isDefaultPlanFlow() && /signup-account/g.test(pathname);

export const trackGlobalClick = (itemTitle, props = {}) => {
  trackEvent(TRACKING_ENUMS.EVENT_GLOBAL_NAV_CLICK, {
    page_item_title: itemTitle,
    ...props,
  });
};

const trackUpgradeClick = (text) => {
  trackEvent(TRACKING_ENUMS.EVENT_UPGRADE_CLICKED, {
    page_item_title: text,
    page_action_location: 'global nav',
  });
  trackEvent(TRACKING_ENUMS.EVENT_PURCHASE_CTA_CLICKED, {
    page_button_text: text,
    page_action_location: 'global nav',
    page_item_title: 'join fox nation',
  });
};

export function HeaderComponent() {
  // state
  const [showPromoBanner, setPromoBanner] = useState(false);
  const [showSignUpButton, setSignUpButton] = useState(false);
  const { scrolledClassName } = useScroll({ className: 'headerScrolled' });
  const { pathname = '', search = '' } = useLocation();

  // redux selector
  const user = useSelector((s) => s.user);
  const purchaseMode = useSelector((s) => s.purchaseMode);
  const locale = useSelector((s) => s.appConfig.strings);
  const defaultConfig = useSelector((s) => s.appConfig.defaultConfig);
  const routes = useSelector((s) => s.appConfig.routes);
  const pageMeta = useSelector((s) => s.appConfig.pageMeta);
  const pageLayout = useSelector((s) => s.pageLayout);
  const secondScreen = useSelector((s) => s.secondScreen);
  const isBango = useSelector((s) => s.integrations.bango.subscriptionId);
  const hasBangoRedirect = useSelector((s) => s.integrations.bango.redirectUrl);

  const { device, isDeviceActivation } = secondScreen;
  const { onHeaderClose } = pageLayout;
  const { requirePlanSelection = true } = purchaseMode;
  const { showUserMenuOnPurchaseFlow, rootDomain, showHeaderSignUp } = defaultConfig;
  const isAuthenticated = user.isAuthenticated || user.hasMvpd;

  const { isPurchaseFunnelRoute, isGiftingRoute, isPromoBannerRoute, isNoSignInButtonRoute, isNoSignUpButtonRoute } =
    useMemo(() => getHeaderRouteStateByPathname(pathname), [pathname]);

  const hasNoSignUpButtonPerRedesign = useMemo(() => noSignUpButtonPerRedesign(pathname), [pathname]);

  // sign up button
  useEffect(() => {
    const mayShowSignUpButton =
      !isAuthenticated &&
      !onHeaderClose &&
      !isGiftingRoute &&
      !isDeviceActivation &&
      !isNoSignInButtonRoute &&
      !isBango &&
      !hasNoSignUpButtonPerRedesign &&
      !isNoSignUpButtonRoute;
    const showSignUpButton = showHeaderSignUp && requirePlanSelection && mayShowSignUpButton;
    setSignUpButton(showSignUpButton);
  }, [
    isAuthenticated,
    onHeaderClose,
    showHeaderSignUp,
    requirePlanSelection,
    isGiftingRoute,
    isDeviceActivation,
    isNoSignInButtonRoute,
    isBango,
    isNoSignUpButtonRoute,
    hasNoSignUpButtonPerRedesign,
  ]);

  // promo banner
  useEffect(() => {
    const showPromoBanner = locale.headerPromoBanner && isPromoBannerRoute;
    setPromoBanner(showPromoBanner);
  }, [pageLayout, locale, isPromoBannerRoute]);

  // headerClass
  const headerClass = useMemo(() => {
    const promoBannerClass = showPromoBanner ? 'promoBanner' : '';
    return [scrolledClassName, promoBannerClass].filter(Boolean).join(' ');
  }, [scrolledClassName, showPromoBanner]);

  if (isMobile.mobile && pathname.includes('/account/signup-confirmation')) {
    return <MobileWebConfirmationHeader />;
  }

  const isSignUp = pathname.includes('/account/signup-plans');
  const variantTitle = 'Choose a Plan';

  const trialRoute = isDefaultPlanFlow() ? routes.signupCreateAccount : routes.signupPlans;

  const signUpButtonText = 'Start Your Free Trial'; //'Join Now'
  const isSheerVerification = pathname.includes(routes.sheerIdVerification);

  return (
    <Fragment>
      <header className={`globalHeader ${headerClass}`}>
        <div className="globalHeaderCenter">
          <div className="headerSideLeft">
            {(!isSheerVerification || (!hasBangoRedirect && isSheerVerification)) && (
              <>
                <SmartLink
                  linkUrl={rootDomain}
                  text={<Logo className="headerLogo" />}
                  className="headerLogoLink hasVariant"
                  onClick={() => trackGlobalClick('Network Logo')}
                  reload
                />
                <SmartLink
                  linkUrl={rootDomain}
                  text={<Logo className="headerLogoVariant" isVariant />}
                  className="headerLogoVariantLink"
                  onClick={() => trackGlobalClick('Network Logo')}
                  reload
                />
              </>
            )}
            {hasBangoRedirect && isSheerVerification && (
              <div className="headerLogoLink unclickableBranding">
                <Logo className="headerLogo" />
                <Logo className="headerLogoVariant" isVariant />
              </div>
            )}
          </div>
          <div className="headerCenter"></div>
          <div className="headerSideRight">
            {showSignUpButton && (
              <Link
                className="headerSignupButton"
                to={{ pathname: trialRoute, state: { page_source: 'global nav' } }}
                onClick={() => trackUpgradeClick(signUpButtonText)}
              >
                {signUpButtonText}
              </Link>
            )}
            {pathname !== routes.signin &&
              (!isSheerVerification || (!hasBangoRedirect && isSheerVerification)) &&
              showUserMenuOnPurchaseFlow &&
              !isDeviceActivation &&
              !onHeaderClose &&
              !isNoSignInButtonRoute &&
              !(isPurchaseFunnelRoute && isBango) && (
                <UserMenu
                  segmentProps={{ page_start_source: 'global nav' }}
                  pathname={pathname}
                  locationSearch={search}
                />
              )}
            {isSignUp && !isAuthenticated && (
              <div className="variantTitleContainer">
                <h1 className="variantTitle">{variantTitle}</h1>
              </div>
            )}
            {onHeaderClose && (
              <button className="headerClose" onClick={onHeaderClose} type="button">
                <img src="/account/static/images/FN/close.svg" width="24" height="24" alt="Close" />
              </button>
            )}
            {isDeviceActivation && (
              <div className="secondScreen">
                Activate
                {device && (
                  <img
                    className="device"
                    src={`/account/static/images/deviceIcons/secondScreen/${device}.png`}
                    alt="Device"
                  />
                )}
              </div>
            )}
            {pageMeta.chatURL && (
              <div className="headerFaq">
                <SmartLink linkUrl={pageMeta.chatURL} text="faq" className="headerFaqBtn" target="_blank" reload />
              </div>
            )}
          </div>
        </div>
        {showPromoBanner && <div className="globalHeaderPromoBanner">{HTMLParser(locale.headerPromoBanner)}</div>}
      </header>
      <RedfastZoneByPathname />
    </Fragment>
  );
}
const Header = HeaderComponent;
export default Header;
